import { Button, Input, ISelectOption, MessageHelper, Select } from '@aon/aon-tws-common-ui';
import { labelLarge, labelMedium } from '@aon/aon-tws-common-ui/build/lib/globalCss';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { SurveyTheme, Theme, Themes } from '../../../models';
import { SurveyThemeValidationResult } from '../../../store/SurveyThemeSlice';
import { Col3Col9 } from '../../common';
import { SurveyPreviewModal } from './SurveyPreviewModal';

type SurveyThemeValueChangeEvent = React.ChangeEvent<Omit<HTMLInputElement, 'name'> & { name: keyof Theme }>;

type SurveyThemeSectionProps = {
	disabled?: boolean;
	themes: Themes;
	surveyTheme?: SurveyTheme;
	validationResult: SurveyThemeValidationResult;
	onThemeChange: (themeName: string) => void;
	onThemeValueChange: (event: SurveyThemeValueChangeEvent) => void;
};

const Fieldset = styled.fieldset.attrs({
	className: 'form-group',
})`
	position: relative;
`;
Fieldset.displayName = 'Fieldset';

const Legend = styled.legend<{ $size?: 'md' | 'lg' }>`
	${(props) => {
		switch (props.$size) {
			case 'md':
				return labelMedium;
			case 'lg':
				return `
					${labelLarge}
					line-height: 40px !important;
				`;
			default:
				return;
		}
	}}
`;
Legend.displayName = 'Legend';

const ThemeCol3Col9 = styled(Col3Col9)`
	background-color: #f1f1f3;
	padding: 1rem 0.5rem 0 0.5rem;
	margin-bottom: 2rem;
`;

const Row = styled.div.attrs({ className: 'row' })``;
Row.displayName = 'Row';

const Col = styled.div.attrs({ className: 'col' })``;
Col.displayName = 'Col';

const SurveyPreviewModalButton = styled(Button)`
	position: absolute;
	top: -48px;
	right: 0;
`;

const SurveyThemeSection = ({
	disabled,
	themes,
	surveyTheme,
	validationResult,
	onThemeChange,
	onThemeValueChange: handleThemeValueChange,
}: SurveyThemeSectionProps) => {
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

	const presetThemeOptions = useMemo(() => {
		const options = Object.keys(themes)
			.sort((a, b) => {
				if (a === Constants.DEFAULT_THEME_NAME) {
					return -1;
				}

				if (b === Constants.DEFAULT_THEME_NAME) {
					return 1;
				}

				return a.localeCompare(b);
			})
			.map(
				(name, index) =>
					({
						name,
						displayText: name,
						displayOrder: index,
					} as ISelectOption),
			);

		const customOption = {
			name: Constants.CUSTOM_THEME_NAME,
			displayText: Constants.CUSTOM_THEME_NAME,
			displayOrder: options.length,
			disabled: true,
		} as ISelectOption;

		return options.concat(customOption);
	}, [themes]);

	const handlePresetThemeChange = (value: string | string[] | undefined) => {
		if (typeof value !== 'string') {
			return;
		}

		onThemeChange(value);
	};

	return (
		<Fieldset disabled={disabled}>
			<Legend $size='lg'>{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_COLOR}</Legend>
			<SurveyPreviewModalButton color='primary' onClick={() => setIsPreviewModalOpen(true)}>
				{ContentConstants.BUTTONS_PREVIEW}
			</SurveyPreviewModalButton>
			<SurveyPreviewModal isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} />
			<ThemeCol3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_PRESETS}>
				<p id='description-preset-themes'>{ContentConstants.SECTION_DESCRIPTION_SURVEY_THEME_PRESETS}</p>
				<Select
					disabled={disabled}
					className='tws-theme'
					selectSize='sm'
					displayText={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_PRESETS}
					items={presetThemeOptions}
					selectedValue={surveyTheme?.name ?? Constants.DEFAULT_THEME_NAME}
					onApply={handlePresetThemeChange}
				/>
			</ThemeCol3Col9>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_PRIMARY_COLOR}>
				<p id='description-primary-color'>{ContentConstants.SECTION_DESCRIPTION_SURVEY_THEME_PRIMARY_COLOR}</p>
				<Input
					type='color'
					aria-label={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_PRIMARY_COLOR}
					aria-labelledby='description-primary-color'
					name='primaryColor'
					value={surveyTheme?.primaryColor ?? themes[Constants.DEFAULT_THEME_NAME].primaryColor}
					onChange={handleThemeValueChange}
				/>
			</Col3Col9>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BACKGROUND_COLOR}>
				<p id='description-background-color'>{ContentConstants.SECTION_DESCRIPTION_SURVEY_THEME_BACKGROUND_COLOR}</p>
				<Input
					type='color'
					aria-label={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BACKGROUND_COLOR}
					aria-labelledby='description-background-color'
					name='backgroundColor'
					value={surveyTheme?.backgroundColor ?? themes[Constants.DEFAULT_THEME_NAME].backgroundColor}
					onChange={handleThemeValueChange}
				/>
			</Col3Col9>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BUTTONS}>
				<Fieldset
					name='primaryButton'
					aria-describedby='description-primary-button'
					aria-invalid={!!validationResult.failures.primaryButton?.length}
				>
					<Legend $size='md'>{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_PRIMARY_BUTTON}</Legend>
					<p id='description-primary-button'>{ContentConstants.SECTION_DESCRIPTION_SURVEY_THEME_PRIMARY_BUTTON}</p>
					<Row>
						<Col>
							<label>
								{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BUTTON_COLOR}
								<Input
									type='color'
									className='ml-3'
									name='primaryButtonColor'
									value={surveyTheme?.primaryButtonColor ?? themes[Constants.DEFAULT_THEME_NAME].primaryButtonColor}
									onChange={handleThemeValueChange}
								/>
							</label>
						</Col>
						<Col>
							<label>
								{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BUTTON_TEXT_COLOR}
								<Input
									type='color'
									className='ml-3'
									name='primaryButtonTextColor'
									value={surveyTheme?.primaryButtonTextColor ?? themes[Constants.DEFAULT_THEME_NAME].primaryButtonTextColor}
									onChange={handleThemeValueChange}
								/>
							</label>
						</Col>
					</Row>
					{!!validationResult.failures.primaryButton?.length && (
						<Row>
							<Col>{MessageHelper.getFormattedErrorMessages(validationResult.failures.primaryButton)}</Col>
						</Row>
					)}
				</Fieldset>
				<Fieldset
					name='secondaryButton'
					aria-describedby='description-secondary-button'
					aria-invalid={!!validationResult.failures.secondaryButton?.length}
				>
					<Legend $size='md'>{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_SECONDARY_BUTTON}</Legend>
					<p id='description-secondary-button'>{ContentConstants.SECTION_DESCRIPTION_SURVEY_THEME_SECONDARY_BUTTON}</p>
					<Row>
						<Col>
							<label>
								{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BUTTON_COLOR}
								<Input
									type='color'
									className='ml-3'
									name='secondaryButtonColor'
									value={surveyTheme?.secondaryButtonColor ?? themes[Constants.DEFAULT_THEME_NAME].secondaryButtonColor}
									onChange={handleThemeValueChange}
								/>
							</label>
						</Col>
						<Col>
							<label>
								{ContentConstants.SUBSECTIONHEADERS_SURVEY_THEME_BUTTON_TEXT_COLOR}
								<Input
									type='color'
									className='ml-3'
									name='secondaryButtonTextColor'
									value={surveyTheme?.secondaryButtonTextColor ?? themes[Constants.DEFAULT_THEME_NAME].secondaryButtonTextColor}
									onChange={handleThemeValueChange}
								/>
							</label>
						</Col>
					</Row>
					{!!validationResult.failures.secondaryButton?.length && (
						<Row>
							<Col>{MessageHelper.getFormattedErrorMessages(validationResult.failures.secondaryButton)}</Col>
						</Row>
					)}
				</Fieldset>
			</Col3Col9>
		</Fieldset>
	);
};

export { SurveyThemeSection, type SurveyThemeValueChangeEvent, type SurveyThemeSectionProps };
